import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Icon,
  IconButton,
  Heading,
  Text,
  Stack,
  Spacer,
  StackSeparator,
} from '@chakra-ui/react';
import { NavLink, useParams } from 'react-router-dom';
import { ContentType } from '@/lib/types';
import {
  PlusIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  ShareIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import QuickSearchForm from '@/components/ui/quick-search-form';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import CreateContentTypeModal from '@/pages/workspace/create-content-type-modal';
import ShareLink from './share-link';
import useContentTypesStore from '@/utils/use-content-type';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useWorkspace from '@/lib/hooks/use-workspace';
import api from '@/utils/api';

interface Type extends ContentType {
  workspace_content_type_id: number;
  workspace_content_type_reference_id: string;
}

interface Props {
  item: Type;
}

const Item = ({ item }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [shareLinkModal, setShareLinkModal] = useState(false);
  const [selectedType, setSelectedType] = useState<Type | undefined>();
  const { workspaceId } = useParams();
  const { contentTypes, loadContentTypes } = useContentTypesStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteBaseModal, setShowDeleteBaseModal] = useState(false);

  const fetchLoadTypes = async () => {
    if (workspaceId) {
      await loadContentTypes(workspaceId);
    }
  };

  const onDelete = () => {
    if (workspaceId && item.workspace_id === parseInt(workspaceId, 10)) {
      setShowDeleteModal(true);
    } else {
      setShowDeleteBaseModal(true);
    }
  };

  async function handleDelete() {
    try {
      setIsLoading(true);

      const promise = api.delete(`content-types/${item.id}`);

      toaster.promise(promise, {
        success: {
          title: 'Content library was removed successfully',
        },
        error: {
          title: 'Failed to remove content library',
        },
        loading: { title: 'Removing...', description: 'Please wait' },
      });

      await promise;
      setShowDeleteModal(false);
      fetchLoadTypes();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete content library',
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteBase() {
    try {
      if (contentTypes && contentTypes.length > 0 && item.id) {
        const baseContentType = contentTypes.find(
          (type: ContentType) => type.id === item.id,
        ) as Type;

        if (baseContentType.workspace_content_type_id) {
          setIsLoading(true);

          const promise = api.delete(
            `workspace-content-types/${baseContentType.workspace_content_type_id}`,
          );

          toaster.promise(promise, {
            success: {
              title: 'Content library was removed successfully',
            },
            error: {
              title: 'Failed to remove content library',
            },
            loading: { title: 'Removing...', description: 'Please wait' },
          });

          await promise;
          setShowDeleteBaseModal(false);
          fetchLoadTypes();
        }
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete content library',
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleGenerateLink() {
    setShareLinkModal(true);
    setSelectedType(item);
  }

  return (
    <>
      <Stack>
        <Flex direction='row' alignItems='center'>
          <NavLink key={item.id} to={`/${workspaceId}/content-types/${item.id}/edit`}>
            <Text color={'gray.700'} fontSize={'md'} fontWeight={'medium'}>
              {item.name}
              {item.workspace_content_type_id && item.workspace_content_type_id > 0 ? (
                <Badge ml={2} variant='surface' size='xs'>
                  Base
                </Badge>
              ) : null}
            </Text>
          </NavLink>
          <Spacer />
          <Stack direction='row' display={{ base: 'none', md: 'block' }}>
            <Tooltip content={'Edit'}>
              <IconButton variant={'ghost'} aria-label={'Edit'} size={'sm'}>
                <NavLink key={item.id} to={`/${workspaceId}/content-types/${item.id}/edit`}>
                  <Icon as={PencilIcon} />
                </NavLink>
              </IconButton>
            </Tooltip>
            <Tooltip content={'Generate link'}>
              <IconButton
                variant={'ghost'}
                aria-label={'Generate link'}
                size={'sm'}
                onClick={handleGenerateLink}
              >
                <Icon as={ShareIcon} />
              </IconButton>
            </Tooltip>
            <Tooltip content={'Delete'}>
              <IconButton variant={'ghost'} aria-label={'Delete'} size={'sm'} onClick={onDelete}>
                <Icon as={TrashIcon} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box display={{ base: 'block', md: 'none' }}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton variant='outline' aria-label='Menu' size='sm'>
                  <Icon as={Bars3Icon} />
                </IconButton>
              </MenuTrigger>
              <MenuContent>
                <MenuItem value='edit' asChild>
                  <NavLink key={item.id} to={`/${workspaceId}/content-types/${item.id}/edit`}>
                    <Icon as={PencilIcon} w={4} h={4} />
                    Edit
                  </NavLink>
                </MenuItem>
                <MenuItem value='generate-link' onClick={handleGenerateLink}>
                  <Icon as={ShareIcon} w={4} h={4} />
                  Generate link
                </MenuItem>
                <MenuItem value='delete' onClick={onDelete}>
                  <Icon as={TrashIcon} w={4} h={4} />
                  Delete
                </MenuItem>
              </MenuContent>
            </MenuRoot>
          </Box>
        </Flex>
      </Stack>

      <DestructiveModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(!showDeleteModal)}
        onConfirm={() => handleDelete()}
        title={'Delete content library'}
        children={`Remove ${item.name} from content libraries?`}
        confirmText={'Delete'}
        isLoading={isLoading}
      />

      <DestructiveModal
        isOpen={showDeleteBaseModal}
        onCancel={() => setShowDeleteBaseModal(!showDeleteBaseModal)}
        onConfirm={() => handleDeleteBase()}
        title={'Delete content library'}
        children={`Remove ${item.name} from content libraries?`}
        confirmText={'Delete'}
        isLoading={isLoading}
      />

      {shareLinkModal && selectedType && (
        <ShareLink
          isOpen={shareLinkModal}
          onClose={() => setShareLinkModal(!shareLinkModal)}
          item={selectedType}
          reloadContents={fetchLoadTypes}
        />
      )}
    </>
  );
};

export default function ListContentLibrary() {
  const { contentTypes } = useContentTypesStore();
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const [searchResults, setSearchResults] = useState<Type[]>([]);

  useEffect(() => {
    setSearchResults(contentTypes);
  }, [contentTypes]);

  const { workspaceId } = useParams();
  const { workspace } = useWorkspace();
  const createModal = useModalToggle();

  const handleSearch = (search: string) => {
    if (search.length === 0) return setSearchResults(contentTypes);
    const searchResults = contentTypes.filter((item) => {
      return item.name.toLowerCase().includes(search.toLowerCase());
    });
    setSearchResults(searchResults);
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'4xl'}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'normal', md: 'center' }}
              mb={4}
            >
              <Heading size='3xl' mb={{ base: 4, md: 0 }}>
                Content libraries
              </Heading>
              <Spacer />
              {isAdmin && !isCancelled && (
                <Stack gap={4} direction={{ base: 'column', md: 'row' }}>
                  <Button colorPalette='brand' onClick={createModal.open}>
                    <Icon as={CheckIcon} w={4} h={4} />
                    Add from library
                  </Button>
                  <Button colorPalette='brand' asChild>
                    <NavLink to={`/${workspaceId}/content-types/create`}>
                      <Icon as={PlusIcon} w={4} h={4} />
                      Create custom library
                    </NavLink>
                  </Button>
                </Stack>
              )}
            </Flex>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'4xl'}>
            <Box mb={8}>
              <QuickSearchForm handleSearch={handleSearch} />
            </Box>
            <Stack gap={4} separator={<StackSeparator />}>
              {searchResults.map((item) => (
                <Item key={item.id} item={item} />
              ))}
            </Stack>
          </Container>
        </Card.Body>
      </Card.Root>

      {workspace && workspaceId && workspace.sport_type_id && (
        <CreateContentTypeModal
          workspaceId={parseInt(workspaceId, 10)}
          sportTypeId={workspace.sport_type_id}
          {...createModal.props}
          onSuccess={createModal.close}
        />
      )}
    </WorkspaceLayout>
  );
}
